import { buildDashboardReportParams, getSessionDefaultProjectId } from "../../utils/dashboardSession";
import { isMobileAssetDefaultDashboard } from "../../config/utils";
import { getAppliedListFilterCount, getAppliedWildCardFilterCount, stripEmptyListFilterParams } from "../../utils/filtersUtils";
import { filterFields as assetFilterFields } from "../assets/components/FilterModal/constants";
import { buildAddonFilters, buildMobileAssetFilterParams, splitVoiceFilters } from "../assetSurveyProject/utils";
import { assetSurveyStatus, surveyStatus, surveyTypes } from "../assetSurveyProject/constants";
import { DEFAULT_ASSET_DASHBOARD_MAP_FILTERS } from "./constants";
import { GEO_HIERARCHY_GROUP_BY_OPTIONS } from "../assetManagement/components/charts/groupedAssetChart";
import store from "../../store/configureStore";

export const ASSET_DASHBOARD_FROM = "assetDashboard";
export const MOBILE_ASSET_DASHBOARD_FROM = "mobileAssetDashboard";

export const isMobileAssetDashboard = (params = {}) => params?.from === MOBILE_ASSET_DASHBOARD_FROM;

export const getAssetDashboardFrom = (params = {}) => (isMobileAssetDashboard(params) ? MOBILE_ASSET_DASHBOARD_FROM : ASSET_DASHBOARD_FROM);

/** Both asset dashboards share /assetdashboard, so the variant rides in the URL to survive a reload. */
export const getAssetDashboardFromUrl = (search = window.location.search) =>
    new URLSearchParams(search).get("from") === MOBILE_ASSET_DASHBOARD_FROM ? MOBILE_ASSET_DASHBOARD_FROM : null;

export const resolveAssetDashboardFrom = (params = {}, search = window.location.search) => {
    const fromUrl = getAssetDashboardFromUrl(search);
    if (fromUrl) return fromUrl;
    if (isMobileAssetDashboard(params)) return MOBILE_ASSET_DASHBOARD_FROM;
    return isMobileAssetDefaultDashboard() ? MOBILE_ASSET_DASHBOARD_FROM : ASSET_DASHBOARD_FROM;
};

export const resolveAssetDashboardMapFilters = (mapFilters = {}) => ({
    ...DEFAULT_ASSET_DASHBOARD_MAP_FILTERS,
    ...(mapFilters || {})
});

export const getAssetDashboardMapAssetType = (mapFilters = {}) =>
    resolveAssetDashboardMapFilters(mapFilters).asset_type || DEFAULT_ASSET_DASHBOARD_MAP_FILTERS.asset_type;

const isNonEmptyArray = value => Array.isArray(value) && value.length > 0;

/** Entity ids applied only when drilling down from an Asset Dashboard map pin. */
export const getAssetDashboardMapEntityFilters = (params = {}) => {
    const entityFilters = {};
    if (isNonEmptyArray(params?.building_ids)) {
        entityFilters.building_ids = params.building_ids;
    }
    if (isNonEmptyArray(params?.site_ids)) {
        entityFilters.site_id = params.site_ids;
    }
    if (isNonEmptyArray(params?.region_ids)) {
        entityFilters.region_ids = params.region_ids;
    }
    return entityFilters;
};

const toIdArray = value => {
    if (isNonEmptyArray(value)) return value;
    return value === null || value === undefined || value === "" ? undefined : [value];
};

const getAssetGaugeEntityFilters = (params = {}) =>
    getAssetDashboardMapEntityFilters({
        region_ids: toIdArray(params?.region_id),
        site_ids: toIdArray(params?.site_id),
        building_ids: toIdArray(params?.building_id)
    });

const getJsonbFilterParams = (params = {}) => ({
    ...(params?.jsonb_list && Object.keys(params.jsonb_list).length ? { jsonb_list: params.jsonb_list } : {}),
    ...(params?.jsonb_filters && Object.keys(params.jsonb_filters).length ? { jsonb_filters: params.jsonb_filters } : {})
});

export const getAddonFilterParams = (params = {}) =>
    params?.addon_filters && Object.keys(params.addon_filters).length ? { addon_filters: params.addon_filters } : {};

export const getSourceShapedFilterParams = (chartParams = {}) => {
    const { list = null, filters = null, me = null } = chartParams || {};
    if (!isMobileAssetDashboard(chartParams)) {
        return { list, filters };
    }
    const { list: nonVoiceList, filters: nonVoiceFilters, addon: voiceAddon } = splitVoiceFilters({ list: list || {}, filters: filters || {} });
    let {
        list: mobileList,
        filters: mobileFilters,
        jsonb_list,
        jsonb_filters
    } = buildMobileAssetFilterParams({ list: nonVoiceList, filters: nonVoiceFilters });
    if (me) {
        mobileList = { ...(mobileList || {}), users: { ...(mobileList?.users || {}), id: [me] } };
    }
    return {
        list: mobileList || null,
        filters: Object.keys(mobileFilters || {}).length ? mobileFilters : null,
        ...(jsonb_list ? { jsonb_list } : {}),
        ...(jsonb_filters ? { jsonb_filters } : {}),
        ...buildAddonFilters(!mobileList?.survey_form_for_asset?.status?.some(s => s == 7), voiceAddon)
    };
};

export const getMobileNotRejectedAddonFilters = (chartParams = {}) =>
    getSourceShapedFilterParams(chartParams).addon_filters?.not_rejected ? { addon_filters: { not_rejected: true } } : {};

export const getSourceShapedChartParams = (chartParams = {}) => ({ ...(chartParams || {}), ...getSourceShapedFilterParams(chartParams) });

export const getMobileAssetTableEntityParams = clientId => ({
    projectId: clientId,
    paginationParams: { totalPages: 0, perPage: 40, currentPage: 0, totalCount: 0 },
    selectedRowId: null,
    selectedAssetsId: [],
    params: {
        limit: 40,
        offset: 0,
        search: "",
        filters: null,
        filterKeys: null,
        list: null,
        order: null
    }
});

export const mergeMobileAssetDashboardParams = (requestParams = {}, dashboardFilterParams = {}, { searchKey = "" } = {}) => {
    if (!dashboardFilterParams || !Object.keys(dashboardFilterParams).length) {
        return { ...requestParams };
    }
    let {
        list,
        filters,
        jsonb_list,
        jsonb_filters,
        addon_filters: dashboardAddonFilters,
        from: _from,
        me: _me,
        ...chartNavParams
    } = getAssetDashboardAssetTableFilters(dashboardFilterParams);
    let { not_rejected: _notRejected, ...addon_filters } = dashboardAddonFilters || {};
    if (searchKey) {
        const [table, ...columnParts] = searchKey.includes(".") ? searchKey.split(".") : ["survey_form_for_asset", searchKey];
        const column = columnParts.join(".");

        if (list?.[table]) {
            const { [column]: omitColumn, ...restColumns } = list[table];
            const { [table]: omitTable, ...restList } = list;
            list = Object.keys(restColumns).length ? { ...restList, [table]: restColumns } : restList;
        }
        if (jsonb_list) {
            const { [searchKey]: omitJsonb, ...restJsonbList } = jsonb_list;
            jsonb_list = restJsonbList;
        }
        if (addon_filters) {
            const { [column]: omitAddon, ...restAddon } = addon_filters;
            addon_filters = restAddon;
        }
    }
    const mergedList = { ...(list || {}) };
    Object.entries(requestParams?.list || {}).forEach(([key, value]) => {
        mergedList[key] = value && typeof value === "object" && !Array.isArray(value) ? { ...(mergedList[key] || {}), ...value } : value;
    });
    const mergedJsonbList = { ...(jsonb_list || {}), ...(requestParams?.jsonb_list || {}) };
    const mergedJsonbFilters = { ...(jsonb_filters || {}), ...(requestParams?.jsonb_filters || {}) };
    const mergedAddonFilters = { ...(addon_filters || {}), ...(requestParams?.addon_filters || {}) };
    return {
        ...requestParams,
        ...chartNavParams,
        from: getAssetDashboardFrom(dashboardFilterParams),
        filters: { ...(filters || {}), ...(requestParams?.filters || {}) },
        list: Object.keys(mergedList).length ? mergedList : requestParams?.list,
        ...(Object.keys(mergedJsonbList).length ? { jsonb_list: mergedJsonbList } : {}),
        ...(Object.keys(mergedJsonbFilters).length ? { jsonb_filters: mergedJsonbFilters } : {}),
        ...(Object.keys(mergedAddonFilters).length ? { addon_filters: mergedAddonFilters } : {})
    };
};

export const getAssetDashboardSidePanelParams = (dashboardReducer, { assetClient, chartParams } = {}) => {
    const individualFilters = dashboardReducer?.individualFilters || {};
    const clientIds = assetClient?.id ? [assetClient.id] : undefined;
    const { client_id: _chartClientId, from: _from, ...chartNavParams } = getAssetChartNavigationApiParams(chartParams);
    const mapEntityFilters = getAssetDashboardMapEntityFilters(chartParams);
    const gaugeEntityFilters = getAssetGaugeEntityFilters(chartParams);
    const sidePanelParams = buildDashboardReportParams(
        {
            list: chartParams?.list || {},
            ...(clientIds ? { client_id: clientIds } : {}),
            filters: chartParams?.filters || null,
            ...getJsonbFilterParams(chartParams),
            ...getAddonFilterParams(chartParams),
            ...chartNavParams,
            ...mapEntityFilters,
            ...gaugeEntityFilters,
            from: getAssetDashboardFrom(chartParams)
        },
        {
            masterBuildingTypes: dashboardReducer?.masterFilterList?.building_types || []
        },
        true
    );
    if (isMobileAssetDashboard(chartParams)) {
        const { chart_type, ...mobileSidePanelParams } = sidePanelParams;
        return mobileSidePanelParams;
    }
    return sidePanelParams;
};

export const getAssetDashboardMapParams = (chartParams = {}, { clientId, map_type = "site", asset_type } = {}) => {
    if (!clientId) {
        return null;
    }

    const { filters = null, list = null, has_image, verified_survey } = chartParams || {};
    const params = {
        client_id: clientId,
        map_type,
        from: getAssetDashboardFrom(chartParams),
        ...getJsonbFilterParams(chartParams),
        ...getAddonFilterParams(chartParams)
    };

    if (list && Object.keys(list).length) {
        params.list = list;
    }
    if (filters && Object.keys(filters).length) {
        params.filters = filters;
    }
    if (has_image !== null && has_image !== undefined) {
        params.has_image = has_image;
    }
    if (verified_survey !== null && verified_survey !== undefined) {
        params.verified_survey = verified_survey;
    }
    if (asset_type) {
        params.asset_type = asset_type;
    }

    return stripEmptyListFilterParams(params);
};

const getAssetFilterField = key =>
    assetFilterFields.find(field => field.paramKey === key || field.wildcardParamKey === key || field.searchKey === key || field.key === key);

const humanizeFieldName = name =>
    name
        .split(/[_\s]+/)
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

const getDynamicAssetFilterLabel = (key, dynamicBands) => {
    const fieldName = getDynamicFieldName(key);
    if (!fieldName) return null;
    const match = Object.values(dynamicBands || {})
        .flat()
        .find(field => field?.field_name === fieldName);
    return match?.label || humanizeFieldName(fieldName);
};

const getMobileAssetFilterLabel = key => {
    if (typeof key !== "string") return null;
    const [bucket, ...rest] = key.split(".");
    if (!["asset_static_fields", "asset_dynamic_fields", "ai_analyze_images"].includes(bucket) || !rest.length) return null;
    return humanizeFieldName(rest[rest.length - 1].replace(/_id$/, "")) || null;
};

const getAssetFilterLabel = (key, dynamicBands) =>
    getAssetFilterField(key)?.label || getMobileAssetFilterLabel(key) || getDynamicAssetFilterLabel(key, dynamicBands) || key;

const formatBooleanFilterValue = value => {
    if (value === true || value === "true" || value === "yes") return "Yes";
    if (value === false || value === "false" || value === "no") return "No";
    return value;
};

const formatAssetWildcardValue = filter => {
    const condition = Array.isArray(filter?.filters) ? filter.filters.join(", ") : filter?.filters;
    const value = typeof filter?.key === "object" ? `${filter?.key?.from || ""} - ${filter?.key?.to || ""}` : filter?.key;

    return value ? `${value} [${condition}]` : `[${condition}]`;
};

export const getAssetDashboardAssetTableFilters = (chartParams = {}, { includeChartNavParams = true } = {}) => {
    const { filters = null, list = null, has_image, verified_survey, me = null } = chartParams || {};
    const { client_id: _chartClientId, from: _from, ...chartNavParams } = getAssetChartNavigationApiParams(chartParams);
    const params = {
        ...(includeChartNavParams ? chartNavParams : {}),
        ...getJsonbFilterParams(chartParams),
        ...getAddonFilterParams(chartParams),
        from: getAssetDashboardFrom(chartParams)
    };

    if (list && Object.keys(list).length) {
        params.list = list;
    }
    if (me && isMobileAssetDashboard(chartParams)) {
        params.me = me;
    }
    if (filters && Object.keys(filters).length) {
        params.filters = filters;
    }
    if (has_image !== null && has_image !== undefined) {
        params.has_image = has_image;
    }
    if (verified_survey !== null && verified_survey !== undefined) {
        params.verified_survey = verified_survey;
    }
    Object.assign(params, getAssetDashboardMapEntityFilters(chartParams), getAssetGaugeEntityFilters(chartParams));

    return params;
};

export const mergeAssetDashboardFiltersIntoParams = (pageParams = {}, dashboardFilterParams = {}, options = {}) => {
    const assetFilters = getAssetDashboardAssetTableFilters(dashboardFilterParams, options);
    if (!Object.keys(assetFilters).length) {
        return { ...pageParams };
    }

    const { list, filters, has_image, verified_survey, from: _from, ...chartNavParams } = assetFilters;
    const mergedList = {
        ...(list || {}),
        ...(pageParams.list || {})
    };
    const mergedFilters = {
        ...(filters || {}),
        ...(pageParams.filters || {})
    };
    const result = { ...pageParams, ...chartNavParams };

    if (Object.keys(mergedList).length) {
        result.list = mergedList;
    }
    if (Object.keys(mergedFilters).length) {
        result.filters = mergedFilters;
    }
    if (pageParams.has_image !== null && pageParams.has_image !== undefined) {
        result.has_image = pageParams.has_image;
    } else if (has_image !== null && has_image !== undefined) {
        result.has_image = has_image;
    }
    if (pageParams.verified_survey !== null && pageParams.verified_survey !== undefined) {
        result.verified_survey = pageParams.verified_survey;
    } else if (verified_survey !== null && verified_survey !== undefined) {
        result.verified_survey = verified_survey;
    }

    return result;
};

const hasLocalListFilter = (pageList = {}, key) => Array.isArray(pageList?.[key]) && pageList[key].length > 0;

const hasLocalWildcardFilter = (pageFilters = {}, key) => isAppliedAssetDashboardWildcardFilter(pageFilters?.[key]);

const getDynamicFieldName = key => {
    if (!key || typeof key !== "string") return null;
    if (key.startsWith("asset_dynamic_field__")) return key.slice("asset_dynamic_field__".length);
    if (key.startsWith("dynamic_fields.")) return key.slice("dynamic_fields.".length);
    if (key.startsWith("asset__")) return key.slice("asset__".length);
    return null;
};

const isActiveDropdownField = (fieldKey, searchKey = "") => {
    if (!searchKey || !fieldKey) {
        return false;
    }
    if (fieldKey === searchKey || fieldKey.includes(searchKey) || searchKey.includes(fieldKey)) {
        return true;
    }
    const fieldName = getDynamicFieldName(fieldKey);
    const searchName = getDynamicFieldName(searchKey);
    return Boolean(fieldName && searchName && fieldName === searchName);
};

export const mergeAssetDashboardFiltersForDropdownApi = (
    pageParams = {},
    dashboardFilterParams = {},
    searchKey = "",
    appliedPageParams = null,
    options = {}
) => {
    const assetFilters = getAssetDashboardAssetTableFilters(dashboardFilterParams, options);
    if (!Object.keys(assetFilters).length) {
        return { ...pageParams };
    }

    const { list: dashboardList, filters: dashboardFilters, has_image, verified_survey, from: _from, ...chartNavParams } = assetFilters;
    const appliedParams = appliedPageParams || pageParams;
    const appliedList = appliedParams.list || {};
    const appliedFilters = appliedParams.filters || {};
    const pageList = pageParams.list || {};
    const pageFilters = pageParams.filters || {};
    const mergedList = { ...pageList };
    const mergedFilters = { ...pageFilters };

    Object.entries(dashboardList || {}).forEach(([key, value]) => {
        if (isActiveDropdownField(key, searchKey)) {
            return;
        }
        if (hasLocalListFilter(appliedList, key)) {
            return;
        }
        mergedList[key] = value;
    });

    Object.entries(dashboardFilters || {}).forEach(([key, value]) => {
        if (isActiveDropdownField(key, searchKey)) {
            return;
        }
        if (hasLocalWildcardFilter(appliedFilters, key)) {
            return;
        }
        mergedFilters[key] = value;
    });

    const result = { ...pageParams, ...chartNavParams };

    if (Object.keys(mergedList).length) {
        result.list = mergedList;
    } else {
        delete result.list;
    }

    if (Object.keys(mergedFilters).length) {
        result.filters = mergedFilters;
    } else if (!pageParams.filters) {
        delete result.filters;
    }

    if (pageParams.has_image !== null && pageParams.has_image !== undefined) {
        result.has_image = pageParams.has_image;
    } else if ((appliedParams.has_image === null || appliedParams.has_image === undefined) && has_image !== null && has_image !== undefined) {
        result.has_image = has_image;
    }

    if (pageParams.verified_survey !== null && pageParams.verified_survey !== undefined) {
        result.verified_survey = pageParams.verified_survey;
    } else if (
        (appliedParams.verified_survey === null || appliedParams.verified_survey === undefined) &&
        verified_survey !== null &&
        verified_survey !== undefined
    ) {
        result.verified_survey = verified_survey;
    }

    return result;
};

export const applyAssetDashboardTableParams = (pageParams = {}, dashboardFilterParams = {}, { asset_type } = {}) => ({
    ...mergeAssetDashboardFiltersIntoParams(pageParams, dashboardFilterParams),
    ...(asset_type ? { asset_type } : {}),
    from: getAssetDashboardFrom(dashboardFilterParams)
});

export const applyAssetDashboardDropdownParams = (
    pageParams = {},
    dashboardFilterParams = {},
    { searchKey, asset_type, appliedPageParams } = {}
) => ({
    ...mergeAssetDashboardFiltersForDropdownApi(pageParams, dashboardFilterParams, searchKey, appliedPageParams),
    ...(asset_type ? { asset_type } : {}),
    from: getAssetDashboardFrom(dashboardFilterParams)
});

export const hasAssetDashboardNavFilters = (params = {}) => {
    return Object.keys(getAssetDashboardAssetTableFilters(params)).length > 0;
};

const isAppliedAssetDashboardWildcardFilter = (filter = {}) =>
    filter?.key || filter?.filters?.includes("null") || filter?.filters?.includes("not_null") || filter?.filters?.includes("count_gt_1");

export const hasAssetDashboardListFilter = (dashboardFilterParams = {}, searchKey) => {
    const values = dashboardFilterParams?.list?.[searchKey];
    return Array.isArray(values) && values.length > 0;
};

export const hasAssetDashboardWildcardFilter = (dashboardFilterParams = {}, searchKey) =>
    isAppliedAssetDashboardWildcardFilter(dashboardFilterParams?.filters?.[searchKey]);

export const hasAssetDashboardColumnFilter = (dashboardFilterParams = {}, searchKey) =>
    hasAssetDashboardListFilter(dashboardFilterParams, searchKey) || hasAssetDashboardWildcardFilter(dashboardFilterParams, searchKey);

export const getAssetDashboardAppliedFilterCount = (dashboardFilterParams = {}) => {
    if (isMobileAssetDashboard(dashboardFilterParams)) {
        return getAssetDashboardFilterTags(dashboardFilterParams).length;
    }
    let count = getAppliedWildCardFilterCount(dashboardFilterParams?.filters) + getAppliedListFilterCount(dashboardFilterParams?.list);
    if (dashboardFilterParams?.has_image !== null && dashboardFilterParams?.has_image !== undefined) {
        count += 1;
    }
    if (dashboardFilterParams?.verified_survey !== null && dashboardFilterParams?.verified_survey !== undefined) {
        count += 1;
    }
    return count;
};

const getAppliedFilterName = (paramKey, id) => store.getState()?.assetSurveyProjectReducer?.appliedFilterNames?.[paramKey]?.[id];

const truncateFilterValue = text => (typeof text === "string" && text.length > 80 ? `${text.slice(0, 80)}…` : text);

const MOBILE_ASSET_ID_VALUE_LABELS = {
    "survey_form_for_asset.status": id => assetSurveyStatus?.[id]?.value,
    "survey_form_for_asset.survey_type": id => surveyTypes?.[id],
    "asset_survey_project.survey_status": id => surveyStatus?.[id]?.value
};

const formatMobileAssetListValue = (key, value) =>
    truncateFilterValue(MOBILE_ASSET_ID_VALUE_LABELS[key]?.(value) ?? getAppliedFilterName(key, value) ?? formatBooleanFilterValue(value));

const VOICE_ADDON_LABELS = {
    voice_text: "Voice Transcript",
    voice_text_mobile: "Mobile Voice Transcript",
    has_voice_text: "Has Voice Transcript",
    has_voice_text_mobile: "Has Mobile Voice Transcript"
};

const VOICE_ADDON_PARAM_KEYS = {
    voice_text: "voice_upload.voice_text",
    voice_text_mobile: "voice_upload.voice_text_mobile",
    has_voice_text: "has_voice_text",
    has_voice_text_mobile: "has_voice_text_mobile"
};

const VOICE_LIST_LABELS = Object.fromEntries(Object.entries(VOICE_ADDON_PARAM_KEYS).map(([addonKey, paramKey]) => [paramKey, VOICE_ADDON_LABELS[addonKey]]));

const getVoiceAddonFilterTags = (addon = {}) =>
    Object.entries(addon ?? {})
        .filter(([key]) => VOICE_ADDON_LABELS[key.replace(/_filter$/, "")])
        .map(([key, value]) => ({
            name: VOICE_ADDON_LABELS[key.replace(/_filter$/, "")],
            value: Array.isArray(value)
                ? value.map(v => formatMobileAssetListValue(VOICE_ADDON_PARAM_KEYS[key.replace(/_filter$/, "")], v))
                : typeof value === "boolean"
                  ? [value ? "Yes" : "No"]
                  : [`${value?.key ?? ""} [${value?.filters}]`]
        }));

// A geo hierarchy slice drilled in from the first chart section lands in `list` under the grouping's filterKey
const GEO_LIST_LABELS = Object.fromEntries(
    GEO_HIERARCHY_GROUP_BY_OPTIONS.filter(option => option.filterKey).map(option => [option.filterKey, option.label])
);

export const getAssetDashboardFilterTags = (chartParams, dynamicBands = {}) => {
    const tags = [];
    const isMobile = isMobileAssetDashboard(chartParams);
    const labelOf = key =>
        GEO_LIST_LABELS[key] ||
        (isMobile
            ? VOICE_LIST_LABELS[key] ||
              getMobileAssetFilterLabel(key) ||
              getAssetFilterField(key)?.label ||
              humanizeFieldName((key || "").split(".").pop().replace(/_id$/, "")) ||
              key
            : getAssetFilterLabel(key, dynamicBands));
    const seen = new Set();
    const pushTag = tag => {
        const signature = `${tag.name}::${(Array.isArray(tag.value) ? tag.value : [tag.value]).join(",")}`;
        if (seen.has(signature)) return;
        seen.add(signature);
        tags.push(tag);
    };
    const pushListTag = (key, value) => {
        if (Array.isArray(value) && value.length) {
            pushTag({
                name: labelOf(key),
                value: value.map(v => (isMobile ? formatMobileAssetListValue(key, v) : formatBooleanFilterValue(v)))
            });
        }
    };

    if (isMobile && chartParams?.me) {
        pushTag({ name: "Surveyor", value: ["Me"] });
    }

    Object.entries(chartParams?.list || {}).forEach(([key, value]) => {
        if (key === "users" && chartParams?.me) return;
        if (value && typeof value === "object" && !Array.isArray(value)) {
            Object.entries(value).forEach(([subKey, subValue]) => pushListTag(`${key}.${subKey}`, subValue));
            return;
        }
        pushListTag(key, value);
    });

    Object.entries(chartParams?.jsonb_list || {}).forEach(([key, entry]) => {
        pushListTag(key, Array.isArray(entry?.value) ? entry.value : entry);
    });

    [chartParams?.filters, chartParams?.jsonb_filters].forEach(wildcardFilters => {
        Object.entries(wildcardFilters || {}).forEach(([key, filter]) => {
            if (isAppliedAssetDashboardWildcardFilter(filter)) {
                pushTag({ name: labelOf(key), value: [formatAssetWildcardValue(filter)] });
            }
        });
    });

    ["has_image", "verified_survey"].forEach(key => {
        if (chartParams?.[key] !== null && chartParams?.[key] !== undefined) {
            pushTag({ name: getAssetFilterLabel(key, dynamicBands), value: [formatBooleanFilterValue(chartParams[key])] });
        }
    });

    if (isMobile) {
        getVoiceAddonFilterTags(chartParams?.addon_filters).forEach(pushTag);
    }

    return tags;
};

const ASSET_CHART_NAV_FILTER_LABELS = {
    asset_condition_name: "Condition",
    end_of_life_chart_range: "Useful Life Remaining",
    assets_age_chart_range: "Age",
    sub_system_name: "Sub-System",
    fca_cost_type: "Cost Type"
};

const ASSET_CHART_NAV_FILTER_VALUE_LABELS = {
    fca_cost_type: { with_cost: "With Cost", without_cost: "Without Cost", all: "All" }
};

const ASSET_CHART_NAV_API_KEYS = [
    "asset_condition_name",
    "asset_condition_id",
    "end_of_life_chart_range",
    "assets_age_chart_range",
    "sub_system_id",
    "sub_system_name",
    "sfci_chart_sort_by",
    "sfci_chart_sort_order",
    "fca_cost_type",
    "client_id",
    "from"
];

const hasChartNavValue = value => value !== null && value !== undefined && value !== "";

const ASSET_ENTITY_FILTER_LABELS = {
    project: "Project",
    region: "Region",
    site: "Site",
    building: "Building"
};

const getAssetEntityNavigationTag = (source = {}) => {
    const label = ASSET_ENTITY_FILTER_LABELS[source?.chart_entity_type];
    const entityName = source?.chart_entity_name;
    return label && hasChartNavValue(entityName) ? { name: label, value: [entityName] } : null;
};

export const getAssetEntityNavigationDisplayParams = (source = {}) =>
    getAssetEntityNavigationTag(source) ? { chart_entity_type: source.chart_entity_type, chart_entity_name: source.chart_entity_name } : {};

/**
 * Chart slice + sort/client fields for asset table API calls.
 * Does not include filters/list (those come from Redux for assetDashboard,
 * or are merged separately for from: "charts").
 */
export const getAssetChartNavigationApiParams = (locationState = {}) => {
    if (!locationState || typeof locationState !== "object" || locationState.isInitialView) {
        return {};
    }

    const params = {};
    ASSET_CHART_NAV_API_KEYS.forEach(key => {
        if (hasChartNavValue(locationState[key])) {
            params[key] = locationState[key];
        }
    });
    Object.assign(params, getAssetDashboardMapEntityFilters(locationState), getAssetGaugeEntityFilters(locationState));
    return params;
};

export const getAssetChartNavigationFilterTags = (locationState = {}) => {
    if (!locationState || typeof locationState !== "object" || locationState.isInitialView) {
        return [];
    }

    const tags = [];
    const entityTag = getAssetEntityNavigationTag(locationState);
    if (entityTag) {
        tags.push(entityTag);
    }
    Object.entries(ASSET_CHART_NAV_FILTER_LABELS).forEach(([key, label]) => {
        const value = locationState[key];
        if (hasChartNavValue(value)) {
            tags.push({ name: label, value: [ASSET_CHART_NAV_FILTER_VALUE_LABELS[key]?.[value] || value] });
        }
    });

    // SFCI navigation may only have the id; show it when name is absent
    if (!locationState.sub_system_name && locationState.sub_system_id) {
        tags.push({ name: "Sub-System", value: [locationState.sub_system_id] });
    }

    return tags;
};

export const getAssetChartNavigationFilterCount = (locationState = {}) => getAssetChartNavigationFilterTags(locationState).length;

export const getCombinedAssetDashboardFilterTags = (dashboardFilterParams = {}, locationState = {}, dynamicBands = {}) => [
    ...getAssetDashboardFilterTags(dashboardFilterParams, dynamicBands),
    ...getAssetChartNavigationFilterTags(locationState)
];
